/**
 * **Your pages** — FR-I2, T-4.6; redrawn in Wave 14C (C13, spec §10.5 item 5;
 * T-4.63) in Navigation's manner.
 *
 * A page is an ordered list of typed blocks and nothing else. There is no
 * markup, no custom HTML and no code editor (ADR-0013 §4, dukaan §2): the
 * professional-conduct policy is enforced at render, and a code editor would
 * route around it.
 *
 * **Everything on this screen edits the draft.** The published site does not
 * change until a partner publishes, and the screen says so at the top rather
 * than relying on a firm to infer it from a version number.
 *
 * One list: each page a row with its kind's icon, its title and address, how
 * many sections it has, whether it is in the menu, and **Edit**. The order is
 * the automatic menu's order and the sitemap's, so it is changed here with
 * up/down (drag-free, §10.5 item 5) and **saved as it is pressed** — each move
 * is a `PATCH /pages/{id}` of `display_order`, so this is a per-item page and
 * shows no edit bar (§10.5 item 3). Deleting a page asks first. *Add a page*
 * is a dialog.
 *
 * The standard set — home, about, services, contact, team, blog, privacy, and
 * one per site-visible service — is created in one act, idempotently by slug,
 * because onboarding is resumable and a second press must not double every
 * page.
 */

import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  Field,
  Input,
  Skeleton,
  buttonClass,
} from "@fiveox/ui";
import {
  ArrowDown,
  ArrowUp,
  Briefcase,
  ExternalLink,
  FileText,
  Home,
  Info,
  LayoutList,
  Mail,
  Newspaper,
  Pencil,
  Plus,
  Scale,
  ShieldCheck,
  Sparkles,
  Trash2,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

import { EditPage, useDraftPreviewHref } from "../../components/edit";
import { SITE, useEvaluation } from "../../lib/access";
import { ApiError } from "../../lib/api";
import { site, type Page } from "../../lib/api/site";
import { useSite } from "./useSite";

/* ── pure helpers (tested) ───────────────────────────────────────────────── */

const SLUG_ICON: Record<string, LucideIcon> = {
  home: Home,
  about: Info,
  services: Briefcase,
  team: Users,
  contact: Mail,
  blog: Newspaper,
  privacy: ShieldCheck,
  terms: Scale,
};

/** The icon a page's row wears: the standard page's own, a briefcase for a
 *  service's page, a sheet of paper for the firm's own. */
export function pageIcon(
  page: Pick<Page, "kind" | "slug" | "ref">,
): LucideIcon {
  if (page.kind === "service") return Briefcase;
  if (page.kind === "standard")
    return SLUG_ICON[page.ref ?? page.slug] ?? SLUG_ICON[page.slug] ?? FileText;
  return FileText;
}

/** What kind of page it is, in a word, for the row's badge. */
export function pageKindLabel(kind: Page["kind"]): string | null {
  if (kind === "service") return "Service";
  if (kind === "custom") return "Your own";
  return null;
}

export function sectionCountLabel(count: number): string {
  return count === 0 ? "Empty" : `${count} section${count === 1 ? "" : "s"}`;
}

/**
 * Move the page at `from` to `to`, and say which pages need a new
 * `display_order` for the service's order (`display_order`, then title) to
 * come out the same as the screen.
 *
 * Two adjacent pages with different orders just trade them — two requests.
 * The standard set is numbered 0…7 and services 100…, so ties are possible
 * (and a title then decides); whenever trading would not give a strictly
 * increasing list, the whole list is renumbered 0…n−1 and only the pages whose
 * number actually changes are sent.
 */
export function reorderPages(
  pages: Page[],
  from: number,
  to: number,
): { order: Page[]; patches: { id: string; display_order: number }[] } {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= pages.length ||
    to >= pages.length
  ) {
    return { order: pages, patches: [] };
  }
  const order = [...pages];
  const [moved] = order.splice(from, 1);
  order.splice(to, 0, moved!);

  if (Math.abs(from - to) === 1) {
    const a = pages[from]!;
    const b = pages[to]!;
    if (a.display_order !== b.display_order) {
      const traded = order.map((p) =>
        p.id === a.id
          ? { ...p, display_order: b.display_order }
          : p.id === b.id
            ? { ...p, display_order: a.display_order }
            : p,
      );
      if (strictlyIncreasing(traded)) {
        return {
          order: traded,
          patches: [
            { id: a.id, display_order: b.display_order },
            { id: b.id, display_order: a.display_order },
          ],
        };
      }
    }
  }

  const renumbered = order.map((p, i) => ({ ...p, display_order: i }));
  const patches = renumbered
    .filter(
      (p) =>
        pages.find((was) => was.id === p.id)?.display_order !== p.display_order,
    )
    .map((p) => ({ id: p.id, display_order: p.display_order }));
  return { order: renumbered, patches };
}

function strictlyIncreasing(pages: Page[]): boolean {
  return pages.every(
    (p, i) => i === 0 || p.display_order > pages[i - 1]!.display_order,
  );
}

/** A page's address on the draft preview — `/_preview/<token>/about`. */
export function pagePreviewHref(
  base: string | null,
  href: string,
): string | null {
  if (!base) return null;
  return href === "/" || !href ? base : `${base.replace(/\/$/, "")}${href}`;
}

/* ── the screen ──────────────────────────────────────────────────────────── */

export function SitePages() {
  const { can } = useEvaluation();
  const editable = can(SITE, "can_update");
  const creatable = can(SITE, "can_create");
  const deletable = can(SITE, "can_delete");
  const { vocabulary } = useSite();
  const previewHref = useDraftPreviewHref();
  const navigate = useNavigate();

  const [pages, setPages] = useState<Page[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<Page | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // The list carries each page's blocks, so the section count is read off it —
  // no second request per page.
  const load = useCallback(async () => {
    const body = await site.pages("draft");
    setPages(body.data);
  }, []);

  useEffect(() => {
    // A failed read is not an empty site: offering *Create the usual pages*
    // under it would invite a firm to create pages it already has.
    void load().catch(() => setError("Your pages could not be loaded."));
  }, [load]);

  const createStandard = async () => {
    setBusy(true);
    setError(null);
    try {
      await site.createStandardPages();
      await load();
      setNotice("The usual pages are in your draft. Open one to fill it in.");
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Those pages could not be created.",
      );
    } finally {
      setBusy(false);
    }
  };

  /** Saved as it is pressed; the screen moves first and puts itself right from
   *  the service if a request fails. */
  const move = async (from: number, to: number) => {
    if (!pages) return;
    const { order, patches } = reorderPages(pages, from, to);
    if (patches.length === 0) return;
    setPages(order);
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      for (const patch of patches) {
        await site.updatePage(patch.id, { display_order: patch.display_order });
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "That order could not be saved.",
      );
      await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    setDeleteError(null);
    try {
      await site.deletePage(deleting.id);
      setNotice(`“${deleting.title}” is gone from your draft.`);
      setDeleting(null);
      await load();
    } catch (err) {
      setDeleteError(
        err instanceof ApiError
          ? err.message
          : "That page could not be deleted.",
      );
    } finally {
      setBusy(false);
    }
  };

  const reserved = vocabulary?.reserved_slugs ?? [];
  const list = pages ?? [];
  const inMenu = list.filter((p) => p.in_nav).length;
  const empty = list.filter((p) => (p.blocks?.length ?? 0) === 0).length;

  return (
    <EditPage
      title="Your pages"
      back={{ to: "/site", label: "Your website" }}
      description="Every page of your site, in the order your menu shows them. Every change here goes to your draft — nothing on your live site changes until you publish."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {previewHref && (
            <a
              href={previewHref}
              target="_blank"
              rel="noopener"
              data-pages-preview=""
              className={buttonClass({ variant: "outline", size: "sm" })}
            >
              See it in your draft <ExternalLink size={14} aria-hidden />
            </a>
          )}
          {creatable && list.length > 0 && (
            <Button size="sm" onClick={() => setAdding(true)}>
              <Plus size={14} aria-hidden /> Add a page
            </Button>
          )}
        </div>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {pages === null ? (
        !error && (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )
      ) : list.length === 0 ? (
        <div
          data-pages-empty=""
          className="border-border flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-10 text-center"
        >
          <span className="bg-muted text-primary flex h-12 w-12 items-center justify-center rounded-full">
            <LayoutList size={22} aria-hidden />
          </span>
          <div className="max-w-md space-y-1">
            <h2 className="text-foreground text-base font-semibold">
              Start with the usual pages
            </h2>
            <p className="text-muted-foreground text-sm">
              Home, about, services, team, contact, blog, privacy and terms —
              plus one page for each service you have marked as public. You can
              rename, reorder or delete any of them afterwards.
            </p>
          </div>
          {creatable && (
            <div className="flex flex-wrap justify-center gap-2">
              <Button
                loading={busy}
                loadingText="Creating…"
                onClick={() => void createStandard()}
              >
                <Sparkles size={14} aria-hidden /> Create the usual pages
              </Button>
              <Button
                variant="ghost"
                onClick={() => setAdding(true)}
                disabled={busy}
              >
                Or add one page
              </Button>
            </div>
          )}
        </div>
      ) : (
        <section aria-labelledby="pages-list" className="space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2
              id="pages-list"
              className="text-foreground text-base font-semibold"
            >
              {list.length} page{list.length === 1 ? "" : "s"}
            </h2>
            <p className="text-muted-foreground text-xs" data-pages-summary="">
              {inMenu} in the menu{empty > 0 ? ` · ${empty} still empty` : ""}
            </p>
          </div>
          <ol className="space-y-2" aria-label="Your pages, in order">
            {list.map((page, index) => (
              <PageRow
                key={page.id}
                page={page}
                first={index === 0}
                last={index === list.length - 1}
                editable={editable}
                deletable={deletable && page.kind === "custom"}
                reserveDelete={
                  deletable && list.some((p) => p.kind === "custom")
                }
                busy={busy}
                onUp={() => void move(index, index - 1)}
                onDown={() => void move(index, index + 1)}
                onDelete={() => {
                  setDeleteError(null);
                  setDeleting(page);
                }}
              />
            ))}
          </ol>
          {creatable && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="border-border text-muted-foreground hover:border-primary hover:text-primary flex w-full items-center justify-center gap-2 rounded-xl border border-dashed p-3 text-sm font-medium transition"
            >
              <Plus size={14} aria-hidden /> Add a page
            </button>
          )}
        </section>
      )}

      {adding && (
        <AddPageDialog
          reserved={reserved}
          onClose={() => setAdding(false)}
          onCreated={(page) => {
            setAdding(false);
            navigate(`/site/pages/${page.id}`);
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title={deleting ? `Delete “${deleting.title}”?` : "Delete this page?"}
        confirmLabel="Delete the page"
        cancelLabel="Keep it"
        tone="danger"
        busy={busy}
        busyLabel="Deleting…"
        error={deleteError}
        onConfirm={() => void confirmDelete()}
      >
        <p className="text-muted-foreground text-sm">
          It goes from your draft now, with its sections, and any menu item that
          points to it drops out. Your live site keeps it until you next
          publish.
        </p>
      </ConfirmDialog>
    </EditPage>
  );
}

/* ── one row ─────────────────────────────────────────────────────────────── */

function PageRow({
  page,
  first,
  last,
  editable,
  deletable,
  reserveDelete,
  busy,
  onUp,
  onDown,
  onDelete,
}: {
  page: Page;
  first: boolean;
  last: boolean;
  editable: boolean;
  deletable: boolean;
  /** Some row in the list has a delete button; this one keeps its place. */
  reserveDelete: boolean;
  busy: boolean;
  onUp: () => void;
  onDown: () => void;
  onDelete: () => void;
}) {
  const Icon = pageIcon(page);
  const count = page.blocks?.length ?? 0;
  const kind = pageKindLabel(page.kind);
  const to = `/site/pages/${page.id}`;

  return (
    <li
      data-page-row={page.id}
      className="border-border bg-surface-raised hover:border-primary/40 rounded-xl border p-3 transition sm:p-4"
    >
      <div className="flex items-center gap-3">
        <span className="bg-muted text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
          <Icon size={18} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Link
              to={to}
              className="text-foreground truncate text-sm font-semibold hover:underline"
            >
              {page.title}
            </Link>
            {kind && <Badge size="sm">{kind}</Badge>}
          </div>
          <p className="text-muted-foreground mt-0.5 truncate font-mono text-xs">
            {page.href}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Badge size="sm" tone={count === 0 ? "warning" : "neutral"}>
              {sectionCountLabel(count)}
            </Badge>
            {page.in_nav ? (
              <Badge size="sm" tone="success">
                In the menu
              </Badge>
            ) : (
              <Badge size="sm">Not in the menu</Badge>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center">
          {editable && (
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Move ${page.title} up`}
                disabled={first || busy}
                onClick={onUp}
              >
                <ArrowUp size={14} aria-hidden />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Move ${page.title} down`}
                disabled={last || busy}
                onClick={onDown}
              >
                <ArrowDown size={14} aria-hidden />
              </Button>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Link
              to={to}
              aria-label={`${editable ? "Edit" : "Open"} ${page.title}`}
              className={buttonClass({ variant: "outline", size: "sm" })}
            >
              <Pencil size={14} aria-hidden /> {editable ? "Edit" : "Open"}
            </Link>
            {deletable ? (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete ${page.title}`}
                disabled={busy}
                onClick={onDelete}
              >
                <Trash2 size={14} aria-hidden />
              </Button>
            ) : (
              // Keeps Edit and the arrows in one column down the list when
              // some rows can be deleted and others cannot.
              reserveDelete && <span aria-hidden className="w-9 shrink-0" />
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

/* ── add a page ──────────────────────────────────────────────────────────── */

function AddPageDialog({
  reserved,
  onClose,
  onCreated,
}: {
  reserved: string[];
  onClose: () => void;
  onCreated: (page: Page) => void;
}) {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const body = await site.createPage({
        title: title.trim(),
        kind: "custom",
      });
      onCreated(body.data);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "That page could not be created.",
      );
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} title="Add a page" error={error}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field
          label="What is it called?"
          hint="Its address comes from the name — “Our fees” becomes /our-fees. It opens for you to fill in."
        >
          {(control) => (
            <Input
              {...control}
              autoFocus
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Careers"
            />
          )}
        </Field>
        {reserved.length > 0 && (
          <p className="text-muted-foreground text-xs">
            A few addresses are taken by the product and cannot be used:{" "}
            {reserved.map((slug) => `/${slug}`).join(", ")}.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={busy}
            loadingText="Adding…"
            disabled={!title.trim()}
          >
            Add the page
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
